import { useCallback, useMemo } from 'react';

import AutocompleteWithChips from 'components/Basic/AutocompleteWithChips/AutocompleteWithChips';
import { LookupDateRangeQueryItemDto } from 'types/CodegenLookupTypes';
import { SelectedItemDto } from 'types/CodegenReportTypes';
import { usePostLookupPitcherTeams } from 'hooks/CodegenLookupHooks';
import { useTranslation } from 'react-i18next';

type Props = {
  selectedTeamId: string | undefined;
  onChange: (team: SelectedItemDto) => void;
  dateRanges: LookupDateRangeQueryItemDto[] | undefined;
  levelId: string | undefined;
  gameType: string | undefined;
};

const SelectPitcherTeam = ({ selectedTeamId, onChange, dateRanges, levelId, gameType }: Props) => {
  const { t } = useTranslation();
  const { data, isLoading } = usePostLookupPitcherTeams(dateRanges, levelId, gameType);

  const processedTeams = useMemo(() => {
    if (!data) return [];

    const nameCounts: Record<string, number> = {};

    return data.map((team, i) => {
      const teamName = team.name;

      if (nameCounts[teamName] === undefined) {
        nameCounts[teamName] = 0;
        return { ...team, displayName: teamName, key: i.toString() };
      } else {
        nameCounts[teamName] += 1;
        return {
          ...team,
          displayName: `${teamName} (${nameCounts[teamName]})`,
          key: i.toString(),
        };
      }
    });
  }, [data]);

  const selectedKey = useMemo(() => {
    if (!selectedTeamId) return '';
    return processedTeams.find((team) => team.id === selectedTeamId)?.key ?? '';
  }, [selectedTeamId, processedTeams]);

  const allKeys = useMemo(() => processedTeams?.map((datum) => datum.key) ?? [], [processedTeams]);

  const teamLookup = useMemo(() => new Map(processedTeams?.map((d) => [d.key, d])), [processedTeams]);

  const getTeamName = useCallback(
    (key: string) => (key ? (teamLookup.get(key)?.displayName ?? (t('Nonexistent team') as string)) : ''),
    [teamLookup]
  );

  const getTeamId = useCallback((key: string) => (key ? (teamLookup.get(key)?.id ?? '') : ''), [teamLookup]);

  return (
    <AutocompleteWithChips
      label={t('Team')}
      options={allKeys}
      selected={selectedKey}
      setSelected={(key) => onChange({ id: getTeamId(key), name: getTeamName(key) })}
      getOptionLabel={(key) => (isLoading ? t('Loading teams...') : getTeamName(key))}
      noOptionsText={isLoading ? t('Loading teams...') : t('No teams found')}
      disabled={!dateRanges || !levelId}
      single
    />
  );
};

export default SelectPitcherTeam;
